import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import React from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { Language, WorkflowState } from "../../../interfaces/doc";
import Loading from "../../Loading";
import { defaultWorflowState } from "./ConfigDefault";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "./ConfigFormContext";

const ConfigGeneralite: React.FC = () => {
  const {
    control,
    setValue,
    watch,
    formState: { isLoading },
  } = useFormConfigContext();
  const { isReadOnly } = useConfigModeContext();
  const {
    fields,
    append: addState,
    remove: removeState,
  } = useFieldArray({
    control,
    name: `config.workflow`,
  });

  const states = watch(`config.workflow`);

  const handleCheckboxChange = (
    index: number,
    field: keyof Omit<WorkflowState, "name">,
    checked: boolean
  ) => {
    if (!states) return;
    setValue(
      "config.workflow",
      fields.map((f, i) =>
        checked
          ? i === index
            ? {
                ...states[i],
                is_default: false,
                set_on_validation_success: false,
                set_on_validation_failure: false,
                set_on_validation_error: false,
                [field]: true,
              }
            : {
                ...states[i],
                [field]: false,
              }
          : i === index
            ? {
                ...states[i],
                [field]: false,
              }
            : states[i]
      )
    );
  };
  if (isLoading) {
    return <Loading />;
  }
  return (
    <>
      <Typography className="text-xl font-semibold">Généralités</Typography>
      <Stack rowGap={2} className="px-4 py-4 overflow-y-scroll">
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size="small"
              helperText={error ? error.message : null}
              label="Nom de la configuration"
              error={!!error}
              fullWidth
              variant="outlined"
            />
          )}
        />
        <Controller
          name="config.language"
          control={control}
          render={({ field: { value, ...props } }) => (
            <Select
              size="small"
              className="flex flex-1"
              labelId="query_label"
              value={value}
              {...props}
            >
              {_.values(Language).map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                </MenuItem>
              ))}
            </Select>
          )}
        />

        <div className="flex items-center justify-between">
          <FormLabel className="font-semibold">Statuts</FormLabel>
          {!isReadOnly && (
            <Button
              color="info"
              size="small"
              onClick={() => addState(defaultWorflowState())}
            >
              <Add />
            </Button>
          )}
        </div>
        <Stack spacing={2}>
          {fields.map((field, index) => (
            <Card key={field.id} variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Controller
                      name={`config.workflow.${index}.name`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          disabled={isReadOnly}
                          fullWidth
                          label="Statut"
                          {...field}
                          error={!!error}
                          helperText={error?.message}
                          size="small"
                        />
                      )}
                    />
                    {!isReadOnly && (
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => removeState(index)}
                        sx={{ ml: 1 }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    <Controller
                      name={`config.workflow.${index}.is_default`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={isReadOnly}
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "is_default",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut par défaut"
                        />
                      )}
                    />
                    <Controller
                      name={`config.workflow.${index}.set_on_validation_success`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={isReadOnly}
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "set_on_validation_success",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut dossier validé"
                        />
                      )}
                    />
                    <Controller
                      name={`config.workflow.${index}.set_on_validation_failure`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={isReadOnly}
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "set_on_validation_failure",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut dossier non validé"
                        />
                      )}
                    />
                    <Controller
                      name={`config.workflow.${index}.set_on_validation_error`}
                      control={control}
                      render={({ field: { value } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={isReadOnly}
                              checked={value}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  index,
                                  "set_on_validation_error",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Statut dossier invalidé"
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
export default ConfigGeneralite;
